import { Component } from "react"
import { Coffee } from "lucide-react"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import StateConstants from "src/states/StateContstants"
import ApiHandler from "src/api/ApiHandler"
import { SubscriptionState, openBillingPortal, startSubscriptionCheckout } from "./Subscription"
import { PlanOffer, ensureReportPrices, formatReportPrice, planOffers, singleUnlockPriceEur } from "src/components/reports/reportPricing"
import posthog from "src/consent/posthog"
import { isPropertyUnlockOffered, startPropertyUnlockCheckout } from "./PropertyUnlock"
import { MONTHLY_PLAN, planPriceEur, trackSubscriptionPayFlow } from "./subscriptionPayFlowEvents"
import "./PremiumPage.css"
import "./SubscriptionPlan.css"


interface PlanContentProps {
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
    showSuccess?: boolean
    showCancelled?: boolean
    onPaymentComplete?: () => void
    variant?: "page" | "modal"
    /** The home the panel was opened from, which it can then offer on its own. */
    propertyId?: string | null
    propertyAddress?: PlanAddress | null
}

/** The home's first address line, for the one-home card, and its full address. */
export interface PlanAddress {
    line: string
    full: string
}

interface PlanContentState {
    isPremium: boolean
    accountType: string | null
    subscription: SubscriptionState | null
    isRedirecting: boolean
    error: string | null
    plans: PlanOffer[]
    selectedPlan: OfferedPlanKey
}



/** The plans the pop up sells; any other plan the server lists (lifetime) is left off. */
type OfferedPlanKey = "week" | "year"

const PLAN_LABELS: Record<OfferedPlanKey, { name: string, per: string, cta: string }> = {
    week: { name: "Weekly", per: "/ week", cta: "Start weekly" },
    year: { name: "Yearly", per: "/ year", cta: "Start yearly" },
}

const isOffered = (offer: PlanOffer): offer is PlanOffer & { plan: OfferedPlanKey } => offer.plan in PLAN_LABELS

export function formatPeriodEnd(epochSeconds: number | null): string | null {
    if (!epochSeconds) return null
    return new Intl.DateTimeFormat("en-IE", { day: "numeric", month: "short", year: "numeric" }).format(new Date(epochSeconds * 1000))
}


class PlanContent extends Component<PlanContentProps, PlanContentState> {
    private singleUnlockOptionTracked = false

    constructor(props: PlanContentProps) {
        super(props)
        this.state = {
            isPremium: props.stateHandler.get(StateConstants.IS_PREMIUM) || false,
            accountType: props.stateHandler.get(StateConstants.ACCOUNT_TYPE) || null,
            subscription: props.stateHandler.get(StateConstants.SUBSCRIPTION) || null,
            isRedirecting: false,
            error: null,
            plans: planOffers(),
            selectedPlan: "week",
        }
    }

    private onIsPremium = (isPremium: boolean) => { this.setState({ isPremium: !!isPremium }) }
    private onAccountType = (accountType: string | null) => { this.setState({ accountType: accountType || null }) }
    private onSubscription = (subscription: SubscriptionState | null) => { this.setState({ subscription: subscription || null }) }

    componentDidMount() {
        if (!this.state.isPremium) {
            trackSubscriptionPayFlow("subscription_plans_viewed", {
                surface: this.props.variant || "page",
                plans_offered: this.offeredPlans().length > 0 ? this.offeredPlans().map(offer => offer.plan) : [MONTHLY_PLAN],
                is_logged_in: !!this.props.stateHandler.get(StateConstants.IS_LOGGED_IN),
            })
        }
        ensureReportPrices(() => this.props.apiHandler.getReportPriceTier()).then(() => this.setState({ plans: planOffers() }, this.trackSingleUnlockOptionShown))
        this.trackSingleUnlockOptionShown()
        this.props.stateHandler.addListener(StateConstants.IS_PREMIUM, this.onIsPremium)
        this.props.stateHandler.addListener(StateConstants.ACCOUNT_TYPE, this.onAccountType)
        this.props.stateHandler.addListener(StateConstants.SUBSCRIPTION, this.onSubscription)
    }

    componentWillUnmount() {
        this.props.stateHandler.removeListener(StateConstants.IS_PREMIUM, this.onIsPremium)
        this.props.stateHandler.removeListener(StateConstants.ACCOUNT_TYPE, this.onAccountType)
        this.props.stateHandler.removeListener(StateConstants.SUBSCRIPTION, this.onSubscription)
    }

    currentPlan(): string {
        return this.offeredPlans().length > 0 ? this.state.selectedPlan : MONTHLY_PLAN
    }

    subscribe = async () => {
        const plan = this.offeredPlans().length > 0 ? this.state.selectedPlan : undefined
        trackSubscriptionPayFlow("subscription_pay_button_clicked", {
            surface: this.props.variant || "page",
            plan: plan ?? MONTHLY_PLAN,
            price_eur: planPriceEur(plan),
            option: "all_homes",
            one_home_offered: this.showsSingleUnlock(),
            is_logged_in: !!this.props.stateHandler.get(StateConstants.IS_LOGGED_IN),
        })
        this.setState({ isRedirecting: true, error: null })
        try {
            await startSubscriptionCheckout(this.props.apiHandler, this.props.stateHandler, `plan_${this.props.variant || "page"}`, undefined, plan)
        } catch (error) {
            console.error("Failed to start the subscription checkout", error)
            this.setState({ error: "We couldn't open the checkout just now. Try again in a moment." })
        } finally {
            this.setState({ isRedirecting: false })
        }
    }

    weeklyOffer(): PlanOffer | undefined {
        return this.state.plans.find(offer => offer.plan === "week")
    }

    showsSingleUnlock(): boolean {
        return !this.state.isPremium && !!this.weeklyOffer() && isPropertyUnlockOffered(this.props.stateHandler, this.props.propertyId)
    }

    trackSingleUnlockOptionShown = () => {
        if (this.singleUnlockOptionTracked || !this.showsSingleUnlock()) return
        this.singleUnlockOptionTracked = true
        posthog?.capture("single_unlock_option_shown", { property_id: this.props.propertyId, price_eur: singleUnlockPriceEur() })
    }

    selectPlan = (plan: OfferedPlanKey) => {
        if (plan === this.state.selectedPlan) return
        this.setState({ selectedPlan: plan })
        posthog?.capture("plans_option_selected", { option: "all_homes", plan, property_id: this.props.propertyId ?? null })
    }

    unlockThisHome = async () => {
        if (!this.props.propertyId) return
        this.setState({ isRedirecting: true, error: null })
        try {
            await startPropertyUnlockCheckout(this.props.apiHandler, this.props.stateHandler, this.props.propertyId)
        } catch (error) {
            console.error("Failed to start the unlock checkout", error)
            this.setState({ error: "We couldn't open the checkout just now. Try again in a moment." })
        } finally {
            this.setState({ isRedirecting: false })
        }
    }

    manage = async () => {
        this.setState({ isRedirecting: true, error: null })
        try {
            await openBillingPortal(this.props.apiHandler)
        } catch (error) {
            console.error("Failed to open the billing portal", error)
            this.setState({ error: "We couldn't open your subscription settings just now. Try again in a moment." })
        } finally {
            this.setState({ isRedirecting: false })
        }
    }

    renderActive() {
        if (this.state.accountType === "lifetime") {
            return (
                <div className="subscription-plan-status">
                    <span className="subscription-plan-status-pill is-active">Active</span>
                    <p className="subscription-plan-status-title">Lifetime Access</p>
                    <p className="subscription-plan-status-subtitle">Every figure is unlocked, for good.</p>
                </div>
            )
        }

        const subscription = this.state.subscription
        const periodEnd = formatPeriodEnd(subscription?.currentPeriodEnd ?? null)
        const isEnding = !!subscription?.cancelAtPeriodEnd
        const subtitle = isEnding
            ? (periodEnd ? `Every figure stays unlocked until ${periodEnd}.` : "Every figure stays unlocked until the end of this billing period.")
            : `Every figure is unlocked${periodEnd ? ` · Renews ${periodEnd}` : ""}.`

        return (
            <div className="subscription-plan-status">
                <span className={`subscription-plan-status-pill ${isEnding ? "is-cancelled" : "is-active"}`}>
                    {isEnding ? "Cancelled" : "Active"}
                </span>
                <p className="subscription-plan-status-title">HomeIndex Edge</p>
                <p className="subscription-plan-status-subtitle">{subtitle}</p>
                {subscription && (
                    <button type="button" className="subscription-plan-secondary" onClick={this.manage} disabled={this.state.isRedirecting}>
                        Manage subscription
                    </button>
                )}
            </div>
        )
    }

    offeredPlans() {
        return this.state.plans.filter(isOffered)
    }

    renderPlanSlider() {
        const { selectedPlan } = this.state
        return (
            <div className="subscription-plan-slider" role="radiogroup" aria-label="Choose a plan">
                {this.offeredPlans().map(offer => (
                    <button
                        key={offer.plan}
                        type="button"
                        role="radio"
                        aria-checked={selectedPlan === offer.plan}
                        className={`subscription-plan-slider-option${selectedPlan === offer.plan ? " is-selected" : ""}`}
                        onClick={() => this.selectPlan(offer.plan)}
                    >
                        {PLAN_LABELS[offer.plan].name}
                    </button>
                ))}
            </div>
        )
    }

    renderAllHomes() {
        const { selectedPlan, isRedirecting } = this.state
        const selected = this.state.plans.find(offer => offer.plan === selectedPlan)
        const label = PLAN_LABELS[selectedPlan]
        return (
            <section className="subscription-plan-option is-featured" aria-label="All homes">
                <h2 className="subscription-plan-option-name">All homes</h2>
                {this.renderPlanSlider()}
                <p className="subscription-plan-option-price" aria-live="polite">
                    <strong>{selected ? formatReportPrice(selected.amount_eur) : ""}</strong> <span>{label.per}</span>
                </p>
                <p className="subscription-plan-option-note">Auto-renews</p>
                <button type="button" className="subscription-plan-cta subscription-plan-option-action" onClick={this.subscribe} disabled={isRedirecting}>
                    {label.cta}
                </button>
            </section>
        )
    }

    renderOneHome(oneHomePrice: number) {
        return (
            <section className="subscription-plan-option" aria-label="One home">
                <h2 className="subscription-plan-option-name">One home</h2>
                {this.props.propertyAddress?.line ? (
                    <p className="subscription-plan-option-scope" title={this.props.propertyAddress.full} aria-label={`${this.props.propertyAddress.full} only`}>
                        <span className="subscription-plan-option-address">{this.props.propertyAddress.line}</span>&nbsp;only
                    </p>
                ) : (
                    <p className="subscription-plan-option-scope">This address only</p>
                )}
                <p className="subscription-plan-option-price">
                    <strong>{formatReportPrice(oneHomePrice)}</strong>
                </p>
                <p className="subscription-plan-option-note">One-time payment</p>
                <button type="button" className="subscription-plan-secondary subscription-plan-option-action" onClick={this.unlockThisHome} disabled={this.state.isRedirecting}>
                    Unlock this home
                </button>
            </section>
        )
    }

    renderPlans() {
        const weekly = this.weeklyOffer()
        const oneHomePrice = singleUnlockPriceEur()
        const isChoice = this.showsSingleUnlock() && !!weekly && oneHomePrice != null

        return (
            <article className="subscription-plan-card">
                <div className={`subscription-plan-options${isChoice ? "" : " is-single"}`}>
                    {isChoice && this.renderOneHome(oneHomePrice!)}
                    {this.renderAllHomes()}
                </div>
                <p className="subscription-plan-footnote">Secure checkout by Stripe</p>
            </article>
        )
    }

    renderOffer() {
        if (this.offeredPlans().length > 0) return this.renderPlans()
        return (
            <article className="subscription-plan-card">
                <p className="subscription-plan-price">
                    <strong>€5</strong> <span>/ month</span>
                </p>
                <p className="subscription-plan-coffee">
                    <Coffee size={16} strokeWidth={1.9} aria-hidden /> For the price of a coffee
                </p>
                <button
                    type="button"
                    className="subscription-plan-cta"
                    onClick={this.subscribe}
                    disabled={this.state.isRedirecting}
                >
                    {this.state.isRedirecting ? "Opening checkout…" : "Checkout"}
                </button>
                <p className="subscription-plan-footnote">Secure checkout by Stripe</p>
            </article>
        )
    }

    render() {
        const isModal = (this.props.variant || "page") === "modal"

        return (
            <div className={`subscription-plan${isModal ? " subscription-plan--modal" : ""}`}>
                {this.props.showCancelled && (
                    <div className="premium-cancelled-banner">
                        Checkout was cancelled. You can subscribe anytime.
                    </div>
                )}
                <div className="subscription-plan-header">
                    <h1 className="subscription-plan-title">{this.state.isPremium ? "Your plan" : "HomeIndex Edge"}</h1>
                    <p className="subscription-plan-subtitle">
                        Get ahead of the market. See exact estimates and 5-year forecasts for any home in Ireland, before you buy, sell or bid.
                    </p>
                </div>
                {this.state.isPremium ? this.renderActive() : this.renderOffer()}
                {this.state.error && <p className="subscription-plan-error" role="alert">{this.state.error}</p>}
            </div>
        )
    }
}

export default PlanContent
